import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { DownloadIcon, Plus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";

import { AButton } from "@/components/ui-kit/a-button.tsx";

import { apiGetMedicalReportsOptions, UploadStatus } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { Button } from "../../../components/ui-kit/button";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { formatBytes } from "../../../utils/utils";
import { PatientSubpage } from "../common/patient-subpage";
import { ExtractionModel } from "../documents/extraction-model";
import { ExtractionPanelLazy } from "../documents/extraction-panel-lazy";
import {
  EXTRACTION_POLL_MS,
  ExtractionCell,
  isExtractionRunning,
  ReportWithExtraction,
} from "../documents/extraction-status";
import { AddReportDialog } from "./add-report-dialog";
import { reportTypes, uploadStatusColor, uploadStatuses } from "./enums";
import { MedicalReports } from "./reports";

export const ReportsRoute = observer(function ReportsRoute(props: {
  app: AppModel;
}) {
  const { i18n, t } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const [model] = useState(
    () => new MedicalReports([], api.permissions, api.requireUserUUID()),
  );
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [extractionModel] = useState(() => new ExtractionModel());
  const [selectedReport, setSelectedReport] =
    useState<ReportWithExtraction | null>(null);

  const { data, refetch } = useQuery({
    ...apiGetMedicalReportsOptions({
      query: { patient_ids: [patient.patientId] },
    }),
    // Poll while an upload is being scanned or an AI extraction is running,
    // so a freshly uploaded report advances without a manual refresh. A failed
    // poll stops it: each failure opens the error dialog, so polling through
    // an outage would reopen it every few seconds.
    refetchInterval: (query) => {
      if (query.state.error) return false;
      const reports = (query.state.data?.medical_reports ??
        []) as ReportWithExtraction[];
      const active = reports.some(
        (r) =>
          r.upload_status === UploadStatus.pending || isExtractionRunning(r),
      );
      return active ? EXTRACTION_POLL_MS : false;
    },
  });

  async function openExtraction(report: ReportWithExtraction) {
    setSelectedReport(report);
    await extractionModel.load(report);
  }

  async function handleRetry(report: ReportWithExtraction) {
    await extractionModel.retry(report.uuid);
    await refetch();
  }

  useEffect(() => {
    if (data) model.updateData(data.medical_reports);
  }, [data, model]);

  // The menu's count follows whatever changed the list: an upload reaching
  // ready, a decision, a retry.
  useEffect(() => {
    void patient.fetchAwaitingReview();
  }, [data, patient]);

  return (
    <PatientSubpage title={t`Reports`} loading={!model.initialized}>
      <DataTable
        filterActions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => setShowAddDialog(true)}
          >
            <Trans>Add report</Trans>
          </Button>
        }
        persistState
        editMode="dialog"
        model={model}
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "report", other: "reports" })}`
        }
        columns={[
          {
            header: <Trans>Date</Trans>,
            attr: "report_date",
            type: "date",
            editable: true,
            sort: true,
            initSort: "desc",
            width: 100,
          },
          {
            header: <Trans>Type</Trans>,
            attr: "report_type",
            type: "enum",
            editable: true,
            values: reportTypes,
            filter: {
              kind: "multi",
              group: t`Report`,
              options: new Map(Object.entries(reportTypes)),
            },
          },
          {
            header: <Trans>Comment</Trans>,
            attr: "comment",
            type: "string",
            multiline: true,
            editable: true,
            width: 200,
          },
          {
            header: <Trans>Status</Trans>,
            width: 100,
            cell: ({ item }) => (
              <span className={uploadStatusColor(item.upload_status)}>
                {resolveEnumLabel(uploadStatuses[item.upload_status])}
              </span>
            ),
          },
          {
            header: <Trans>Extraction</Trans>,
            width: 150,
            cell: ({ item }) => (
              <ExtractionCell
                report={item}
                onReview={(report) => void openExtraction(report)}
                onRetry={(report) => void handleRetry(report)}
              />
            ),
          },
          {
            header: <Trans>Filename</Trans>,
            attr: "original_name",
            type: "string",
            multiline: 3,
          },
          {
            header: <Trans>Size</Trans>,
            width: 80,
            cell: ({ item }) =>
              item.size_bytes == null
                ? "—"
                : formatBytes(item.size_bytes, i18n.locale),
          },
          {
            header: <Trans>Download</Trans>,
            width: 50,
            cell: ({ item }) =>
              item.upload_status === UploadStatus.success ? (
                <AButton
                  href={`${api.versionedRoute()}/medical-reports/download/${item.uuid}`}
                  target="_blank"
                  rel="noreferrer"
                  variant="tertiary"
                  size="sm"
                  iconOnly
                  aria-label={t`Download`}
                >
                  <DownloadIcon />
                </AButton>
              ) : null,
          },
        ]}
        onEdit={async (editedField, item) => {
          await patient.updateItem("medical-reports", item.uuid, editedField);
          await refetch();
        }}
        onDelete={async (item) => {
          await patient.deleteItem("medical-reports", item.uuid);
          await refetch();
        }}
      />

      {selectedReport && (
        <ExtractionPanelLazy
          key={selectedReport.uuid}
          app={props.app}
          report={selectedReport}
          model={extractionModel}
          onDecided={() => void refetch()}
          // Nothing on the list may be running, in which case it does not
          // poll, and a failed re-run would still read as ready.
          onRerun={() => void refetch()}
          // The panel renders from the row it was opened with, so the row is
          // patched alongside the reload — otherwise the banner would keep
          // reporting the date the reviewer just replaced.
          onReportUpdated={(reportDate) => {
            setSelectedReport(
              (prev) => prev && { ...prev, report_date: reportDate },
            );
            void refetch();
          }}
          onClose={() => setSelectedReport(null)}
        />
      )}

      {showAddDialog && (
        <AddReportDialog
          app={props.app}
          onClose={() => setShowAddDialog(false)}
          onSaved={refetch}
        />
      )}
    </PatientSubpage>
  );
});
